import React, { useEffect, useSyncExternalStore, useState, useMemo, RefObject } from 'react';
import unescapeHTML from 'lodash/unescape.js';
import throttle from 'lodash/throttle.js';

export function useRestoreScroll() {
  useEffect(() => {
    function saveScroll() {
      history.replaceState({ scroll: window.scrollY }, '');
    }

    window.scrollTo(0, Number(history.state?.scroll) || 0);

    const cb = throttle(saveScroll, 100);

    window.addEventListener('scroll', cb);
    window.addEventListener('resize', cb);
    return () => {
      window.removeEventListener('scroll', cb);
      window.removeEventListener('resize', cb);
    };
  }, []);
}

export function useGoToHash(hash: string) {
  useEffect(() => {
    if (!hash) return;

    document.getElementById(hash.substring(1))?.scrollIntoView();
  }, []);
}

export function getBookHref() {
  let subject = 'Consultation Session';
  let body = `Hello Sean,

I would like to schedule a consultation. Please find my details below:

Name: 


Preferred Date and Time: 


Brief Project Description: 


`;

  subject = encodeURIComponent(subject);
  body = encodeURIComponent(body);

  return `mailto:sean@computing-den.com?subject=${subject}&body=${body}`;
}

export function getPageData<T>(): T {
  const elem = document.getElementById('page-data') as HTMLScriptElement;
  return JSON.parse(unescapeHTML(elem.innerHTML));
}

export const MONTHS_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const DAYS_ABBRS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];

// Custom format: Friday 2 Jun 2024
export function formatDateTime(date: Date) {
  const dayName = DAYS_ABBRS[date.getDay()];
  const day = date.getDate();
  const monthName = MONTHS_ABBRS[date.getMonth()];
  const year = date.getFullYear();

  return `${dayName} ${day} ${monthName} ${year}`;
}

export function useWindowInnerWidth() {
  return useSyncExternalStore(subscribeToWindowResize, getWindowInnerWidth);
}

function subscribeToWindowResize(callback: () => void): () => void {
  window.addEventListener('resize', callback);
  return () => window.removeEventListener('resize', callback);
}

function getWindowInnerWidth(): number {
  return window.innerWidth;
}

export function useDragScroll(props: { dragArea: RefObject<HTMLElement>; scrollArea: RefObject<HTMLElement> }) {
  useEffect(() => {
    let downClientX: number | undefined;
    let downScrollX: number | undefined;
    let moving = false;

    function mouseDown(e: MouseEvent) {
      // On mobile, let the browser handle the drag scroll.
      if (isMobile()) return;

      if (!props.dragArea.current?.contains(e.target as HTMLElement | null)) {
        return reset();
      }

      e.preventDefault();
      e.stopPropagation();
      downClientX = e.clientX;
      downScrollX = props.scrollArea.current!.scrollLeft;
      moving = false;
    }

    function mouseUp(e: MouseEvent) {
      if (moving) {
        e.preventDefault();
        e.stopPropagation();
      }
      reset();
    }

    function mouseMove(e: MouseEvent) {
      if (downClientX === undefined || downScrollX === undefined) return;
      if (!moving && Math.abs(e.clientX - downClientX) > 5) moving = true;
      if (!moving) return;

      e.preventDefault();
      e.stopPropagation();
      const diff = e.clientX - downClientX;
      const left = Math.max(0, downScrollX - diff);
      props.scrollArea.current!.scroll({ left });
    }

    function reset() {
      downClientX = undefined;
      downScrollX = undefined;
      moving = false;
    }

    window.addEventListener('mousedown', mouseDown);
    window.addEventListener('mouseup', mouseUp);
    window.addEventListener('mousemove', mouseMove);
    return () => {
      window.removeEventListener('mousedown', mouseDown);
      window.removeEventListener('mouseup', mouseUp);
      window.removeEventListener('mousemove', mouseMove);
    };
  }, []);
}

export function useClickWithoutDrag<T>(
  onClick: (e: React.MouseEvent<T>) => any,
): [React.MouseEventHandler<T>, React.MouseEventHandler<T>] {
  const [startPos, setStartPos] = useState<[number, number]>();

  function onMouseDown(e: React.MouseEvent<T>) {
    setStartPos([e.clientX, e.clientY]);
  }
  function onMouseUp(e: React.MouseEvent<T>) {
    if (startPos) {
      const x = e.clientX - startPos[0];
      const y = e.clientY - startPos[1];
      setStartPos(undefined);
      if (Math.sqrt(x * x + y * y) <= 5) {
        onClick(e);
      }
    }
  }

  return [onMouseDown, onMouseUp];
}

export function isMobile() {
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
